/**
 * Company-timezone-aware datetime formatting. Loads the company's preferred
 * timezone once on app boot and exposes helpers used across every page that
 * displays a timestamp. Falls back to the browser's local timezone if the
 * preference hasn't loaded yet.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const DEFAULT_TZ = "America/Toronto";

const TimezoneContext = createContext({
  tz: DEFAULT_TZ,
  setTz: () => {},
  refresh: () => {},
});

export function TimezoneProvider({ children }) {
  const [tz, setTz] = useState(() => {
    try { return localStorage.getItem("rf_tz") || DEFAULT_TZ; } catch { return DEFAULT_TZ; }
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/company/preferences");
      const remote = data?.timezone || DEFAULT_TZ;
      setTz(remote);
      try { localStorage.setItem("rf_tz", remote); } catch { /* ignore */ }
    } catch { /* silent — user may not be logged in */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <TimezoneContext.Provider value={{ tz, setTz: (v) => { setTz(v); try { localStorage.setItem("rf_tz", v); } catch {} }, refresh }}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() { return useContext(TimezoneContext); }

/** Format an ISO date-time string in the current company timezone. */
export function fmtDT(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 16).replace("T", " ");
  const zone = tz || readTz();
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d).replace(",", "");
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

/** Date only, no time. */
export function fmtDate(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  const zone = tz || readTz();
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric", month: "2-digit", day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** Time only (HH:MM 24h). */
export function fmtTime(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(11, 16);
  const zone = tz || readTz();
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
  } catch {
    return d.toISOString().slice(11, 16);
  }
}

/** Longer "Started" / "Ended" style — includes seconds. */
export function fmtDTSec(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 19).replace("T", " ");
  const zone = tz || readTz();
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).format(d).replace(",", "");
  } catch {
    return d.toISOString().slice(0, 19).replace("T", " ");
  }
}

/** Common IANA zones — surfaced in the Settings picker. */
export const COMMON_TIMEZONES = [
  "America/Toronto",
  "America/Montreal",
  "America/Halifax",
  "America/St_Johns",
  "America/Winnipeg",
  "America/Regina",
  "America/Edmonton",
  "America/Vancouver",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

function readTz() {
  try { return localStorage.getItem("rf_tz") || DEFAULT_TZ; } catch { return DEFAULT_TZ; }
}
